import {
    experiments,
    getExperiment,
    getExperimentWithExposures,
    useExperiment
} from "@/utils/experiment";

import {
    useEligibleForSimplifiedLandingViewWithBasicTasks,
    useEligibleForBasicSampleTasksForScrumOrSoftwareKanban
} from "../controllers";
import { WizardTemplateOption } from "../utils/types";

export type ExperimentKeys = keyof typeof experiments;

export const useIsJFindInviteStepExperiment = () => {
    const jfindInviteStepExperimentConfig = getExperiment(
        experiments.jfindOnboardingWizardInviteStep
    );
    return (
        jfindInviteStepExperimentConfig.type === "enrolled" &&
        jfindInviteStepExperimentConfig.value
    );
};

export const useIsTeamTypeSpaceRecommendationExperiment = () => {
    const teamTypeSpaceRecommendationExperimentConfig =
        getExperimentWithExposures(
            experiments.jfind1968teamTypeSpaceRecommendation
        );
    return (
        teamTypeSpaceRecommendationExperimentConfig.type === "enrolled" &&
        teamTypeSpaceRecommendationExperimentConfig.value
    );
};

// needs to be cleaned up
export const useIsAllowExtraFieldsExperiment = () => {
    const allowExtraFieldsExperimentConfig = useExperiment(
        experiments.jiraProjectOnboardingExtraFieldsForWizards
    );

    const allowExtraFieldsExperimentConfigIsEnrolled =
        allowExtraFieldsExperimentConfig.type === "enrolled";

    const allowExtraFieldsExperimentConfigFireExposure =
        allowExtraFieldsExperimentConfigIsEnrolled
            ? allowExtraFieldsExperimentConfig.fireExposure
            : undefined;

    return {
        allowExtraFieldsExperimentConfigIsEnrolledInTest:
            allowExtraFieldsExperimentConfigIsEnrolled &&
            allowExtraFieldsExperimentConfig.value,
        allowExtraFieldsExperimentConfigFireExposure
    };
};

export const useIsSimplifiedLandingViewWithBasicTasks = (
    template: WizardTemplateOption
) => {
    const simplifiedListViewWithBasicTasksExperimentConfig = useExperiment(
        experiments.jfindSimplifiedListViewWithBasicTasks
    );
    const { userEligibleForSimplifiedLandingView } =
        useEligibleForSimplifiedLandingViewWithBasicTasks(template);
    if (!userEligibleForSimplifiedLandingView) {
        return {
            simplifiedLandingViewWithBasicTasksIsEnrolled: false,
            simplifiedLandingViewWithBasicTasksFireExposure: undefined
        };
    }

    const simplifiedLandingViewWithBasicTasksIsEnrolled =
        simplifiedListViewWithBasicTasksExperimentConfig.type === "enrolled";

    const simplifiedLandingViewWithBasicTasksFireExposure =
        simplifiedLandingViewWithBasicTasksIsEnrolled
            ? simplifiedListViewWithBasicTasksExperimentConfig.fireExposure
            : undefined;

    return {
        simplifiedLandingViewWithBasicTasksIsEnrolledInTest:
            simplifiedLandingViewWithBasicTasksIsEnrolled &&
            simplifiedListViewWithBasicTasksExperimentConfig.value,
        simplifiedLandingViewWithBasicTasksFireExposure
    };
};

export const useIsBasicSampleTasksForScrumOrSoftwareKanban = () => {
    const basicSampleTasksForScrumOrSoftwareKanbanExperimentConfig =
        useExperiment(
            experiments.jfindBasicSampleTasksForScrumOrSoftwareKanban
        );

    const { userEligibleForBasicSampleTasksForScrumOrSoftwareKanban } =
        useEligibleForBasicSampleTasksForScrumOrSoftwareKanban();

    if (!userEligibleForBasicSampleTasksForScrumOrSoftwareKanban) {
        return {
            basicSampleTasksForScrumOrSoftwareKanbanIsEnrolledInTest: false,
            basicSampleTasksForScrumOrSoftwareKanbanFireExposure: undefined
        };
    }

    const basicSampleTasksForScrumOrSoftwareKanbanIsEnrolled =
        basicSampleTasksForScrumOrSoftwareKanbanExperimentConfig.type ===
        "enrolled";

    const basicSampleTasksForScrumOrSoftwareKanbanFireExposure =
        basicSampleTasksForScrumOrSoftwareKanbanIsEnrolled
            ? basicSampleTasksForScrumOrSoftwareKanbanExperimentConfig.fireExposure
            : undefined;

    return {
        basicSampleTasksForScrumOrSoftwareKanbanIsEnrolledInTest:
            basicSampleTasksForScrumOrSoftwareKanbanIsEnrolled &&
            basicSampleTasksForScrumOrSoftwareKanbanExperimentConfig.value,
        basicSampleTasksForScrumOrSoftwareKanbanFireExposure
    };
};

export const checkExperimentEligibility = (
    isEligible: boolean,
    experimentKey: ExperimentKeys
) => {
    if (!isEligible) return false;
    const experiment = getExperimentWithExposures(experiments[experimentKey]);
    return experiment.type === "enrolled" && experiment.value;
};
